import React, { FC, useEffect, useRef } from 'react';

import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';

import { sanitizeHtml } from 'helpers/sanitize-html.helper';

interface RenderHTMLProps {
    htmlString: string;
}

const scrollbarCss = `
    ::-webkit-scrollbar {
        width: 4px;
        height: 8px;
    }
    ::-webkit-scrollbar-track {
        border-radius: ${aampeTheme.borderRadius.default};
        background: ${aampeTheme.colors.bordersWG01};
    }
    ::-webkit-scrollbar-thumb {
        background: ${aampeTheme.colors.textAccessibleWG01};
        border-radius: ${aampeTheme.borderRadius.default};
    }
`;

// Low-specificity reset so the template's own CSS can override it.
const resetCss = 'body { margin: 0; padding: 0; }';

const RenderHTML: FC<React.PropsWithChildren<RenderHTMLProps>> = ({
    htmlString,
}) => {
    const iframeRef = useRef<any>(null);

    useEffect(() => {
        const iframe = iframeRef.current;
        const iframeDoc =
            iframe.contentDocument || iframe.contentWindow?.document;

        const scrollbarStyle = document.createElement('style');
        scrollbarStyle.innerHTML = scrollbarCss;

        const resetStyle = document.createElement('style');
        resetStyle.innerHTML = resetCss;

        const sanitizedHTML = sanitizeHtml(htmlString);

        iframeDoc.open();
        iframeDoc.write('<!DOCTYPE html>' + sanitizedHTML);
        iframeDoc.close();

        const adjustIframeDimensions = () => {
            const contentHeight = iframeDoc.body.scrollHeight;
            iframe.style.height = contentHeight + 'px';
            // Insert reset first so template CSS takes precedence.
            iframeDoc.head.insertBefore(resetStyle, iframeDoc.head.firstChild);
            iframeDoc.head.appendChild(scrollbarStyle);
        };

        const observer = new ResizeObserver(adjustIframeDimensions);

        if (iframeDoc && iframeDoc.body) {
            observer.observe(iframeDoc.body);
        }

        iframe.addEventListener('load', adjustIframeDimensions);
        iframeDoc.addEventListener('DOMContentLoaded', adjustIframeDimensions);
        window.addEventListener('resize', adjustIframeDimensions);

        return () => {
            observer.disconnect();
            iframe.removeEventListener('load', adjustIframeDimensions);
            iframeDoc.removeEventListener(
                'DOMContentLoaded',
                adjustIframeDimensions,
            );
            window.removeEventListener('resize', adjustIframeDimensions);
        };
    }, [htmlString]);

    return <Iframe ref={iframeRef} sandbox="allow-same-origin allow-popups" />;
};

export default RenderHTML;

const Iframe = styled.iframe({
    border: 'none',
    width: '100%',
    height: '100%',
});

Iframe.defaultProps = {
    sandbox: 'allow-same-origin',
};
